import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { LOGO } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer, AlertTriangle, User, Bus } from "lucide-react";
import { toast } from "sonner";

const MARKS = [["prepared", "Prepared"], ["served", "Served"], ["not_eating", "Not Eating"], ["absent", "Absent"], ["issue", "Issue"]];
const MARK_COLOR = { prepared: "bg-blue-500", served: "bg-emerald-500", not_eating: "bg-slate-400", absent: "bg-amber-500", issue: "bg-coral" };

export default function TodaysLunches() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState({ rows: [], kitchen: [], total: 0 });

  const load = useCallback(() => {
    api.get(`/staff/lunches?date=${date}`).then((r) => setData(r.data)).catch((e) => toast.error(apiErr(e)));
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const mark = async (id, status) => {
    let note = "";
    if (status === "issue") { note = window.prompt("Describe the issue / note:") || ""; }
    try { await api.post(`/staff/lunches/${id}/mark`, { served_status: status, note }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const byMeal = {};
  data.rows.forEach((r) => { (byMeal[r.meal_name] = byMeal[r.meal_name] || []).push(r); });

  return (
    <div className="min-h-screen bg-navy text-white print:bg-white print:text-black">
      <header className="min-h-16 py-2 flex flex-wrap items-center justify-between gap-2 px-4 border-b border-white/10 print:hidden">
        <div className="flex items-center gap-2 shrink-0"><img src={LOGO} className="h-9 w-9 object-contain" alt="logo" /><p className="font-display font-bold text-sm">TODAY'S LUNCHES</p></div>
        <div className="flex flex-wrap items-center gap-1.5">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-lg bg-white/10 border border-white/20 text-white px-2 py-1 text-sm" data-testid="lunches-date" />
          <Button variant="ghost" size="sm" onClick={() => window.print()} className="text-white/80" data-testid="lunches-print"><Printer className="w-4 h-4 mr-1" /> Print</Button>
          <Button variant="ghost" size="sm" onClick={() => nav(["admin", "manager"].includes(user?.role) ? "/admin" : "/staff")} className="text-white/70"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          <Button variant="ghost" size="icon" onClick={async () => { await logout(); nav("/"); }}>✕</Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 print:p-0">
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4 mb-5 print:border-slate-300" data-testid="lunch-kitchen-summary">
          <p className="font-display font-extrabold text-gold text-sm tracking-widest uppercase mb-2">Today's Meal Count</p>
          {data.kitchen.length === 0 ? <p className="text-white/40 text-sm print:text-black">No lunches ordered.</p> :
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">{data.kitchen.map((k) => (
              <div key={k.meal} className="flex justify-between bg-white/5 rounded-lg px-3 py-2 print:border print:border-slate-200"><span className="text-sm print:text-black">{k.meal}</span><span className="font-bold text-turquoise print:text-black">{k.count}</span></div>
            ))}</div>}
        </div>

        {Object.keys(byMeal).sort().map((meal) => (
          <div key={meal} className="mb-6" data-testid={`lunch-group-${meal}`}>
            <p className="font-display font-extrabold text-lg text-turquoise mb-2 border-b border-white/10 pb-1">{meal} · {byMeal[meal].length}</p>
            <div className="space-y-2">
              {byMeal[meal].map((r) => (
                <div key={r.id} className="bg-white/5 border border-white/10 rounded-2xl p-3 print:border-slate-300 print:break-inside-avoid" data-testid={`lunch-row-${r.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center overflow-hidden shrink-0">
                      {r.photo_url ? <img src={r.photo_url} className="w-full h-full object-cover" alt="" /> : <User className="w-5 h-5 text-white/50" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-display font-bold print:text-black">{r.child_name}
                        {r.served_status && <span className={`ml-2 text-[10px] font-bold text-white rounded-full px-2 py-0.5 ${MARK_COLOR[r.served_status]}`}>{r.served_status.replace("_", " ")}</span>}
                      </p>
                      {r.allergies && <p className="text-[12px] text-coral flex items-center gap-1 print:text-red-700"><AlertTriangle className="w-3.5 h-3.5" /> Allergy: {r.allergies}</p>}
                      {r.side && <p className="text-[12px] text-turquoise print:text-black">Side: {r.side}</p>}
                      {r.prep_prefs?.length > 0 && <p className="text-[12px] text-white/70 print:text-black">Prefs: {r.prep_prefs.join(", ")}</p>}
                      {r.notes && <p className="text-[12px] text-white/50 print:text-black">Note: {r.notes}</p>}
                      <p className="text-[11px] text-white/50 print:text-black flex items-center gap-2">
                        {r.transport && r.transport !== "none" ? <span className="flex items-center gap-1"><Bus className="w-3 h-3" /> {r.transport}</span> : "Parent collect"}
                        {r.paid_with_credit ? " · credit" : ""}
                        {r.attendance === "absent" ? <span className="text-amber-400 font-bold"> · ABSENT from Rock — check lunch</span> : ""}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2 print:hidden">
                    {MARKS.map(([k, l]) => (
                      <button key={k} onClick={() => mark(r.id, k)} data-testid={`lunch-mark-${k}-${r.id}`}
                        className={`text-xs font-bold rounded-full px-3 py-1.5 ${r.served_status === k ? MARK_COLOR[k] + " text-white" : "bg-white/10 text-white/70 hover:bg-white/20"}`}>{l}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        {data.total === 0 && <p className="text-white/40 print:text-black">No lunches for this day.</p>}
      </main>
    </div>
  );
}
